import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { auth } from '../firebase';
import {
  FILES_KIND_FILE,
  FILES_KIND_FOLDER,
  FILES_OWNER_SCOPE_COMPANY,
  FILES_OWNER_SCOPE_USER,
  FILES_ROOT_PARENT_ID,
  copyFilesNode,
  createFilesFolder,
  deleteFilesNode,
  formatBulkFilesResultMessage,
  listFilesChildren,
  moveFilesNode,
  runBulkFilesOperation,
  uploadFilesNode,
} from '../lib/companyFiles';

const INTERNAL_DRAG_MIME = 'application/x-shifty-files-nodes';

/**
 * @param {DataTransfer} dt
 * @returns {boolean}
 */
function dataTransferHasFiles(dt) {
  if (!dt) return false;
  if (dt.files && dt.files.length > 0) return true;
  if (dt.items) {
    for (let i = 0; i < dt.items.length; i += 1) {
      if (dt.items[i].kind === 'file') return true;
    }
  }
  const types = dt.types ? Array.from(dt.types) : [];
  return types.includes('Files');
}

/**
 * Company Files + My files hub — browse, upload, drag-move, OS drop, bulk ops.
 * @param {{ userData?: { companyId?: string, uid?: string, role?: string } }} props
 */
export default function FilesHub({ userData }) {
  const companyId = userData?.companyId;
  const uid = userData?.uid || auth.currentUser?.uid || null;
  const role = userData?.role || null;

  const [scopeTab, setScopeTab] = useState(0); // 0 company, 1 my
  const ownerScope =
    scopeTab === 0 ? FILES_OWNER_SCOPE_COMPANY : FILES_OWNER_SCOPE_USER;

  const [folderStack, setFolderStack] = useState([]); // [{id, name}]
  const parentId =
    folderStack.length === 0
      ? FILES_ROOT_PARENT_ID
      : folderStack[folderStack.length - 1].id;

  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [lastClickedId, setLastClickedId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null); // folder id or '__current__'
  const [draggingInternal, setDraggingInternal] = useState(false);

  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveTargets, setMoveTargets] = useState([]); // flat {id, name, depth}
  const [moveTargetsLoading, setMoveTargetsLoading] = useState(false);
  const [moveDestId, setMoveDestId] = useState(FILES_ROOT_PARENT_ID);

  const fileInputRef = useRef(null);
  const dragPayloadRef = useRef([]);

  const refresh = useCallback(async () => {
    if (!companyId) {
      setNodes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const rows = await listFilesChildren({
        companyId,
        ownerScope,
        ownerUid: ownerScope === FILES_OWNER_SCOPE_USER ? uid : null,
        parentId,
      });
      setNodes(rows);
    } catch (err) {
      console.error('FilesHub list failed', err);
      setError(err?.message || 'Could not load files.');
      setNodes([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, ownerScope, parentId, uid]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    setFolderStack([]);
    setSelectedIds(new Set());
    setLastClickedId(null);
  }, [scopeTab]);

  useEffect(() => {
    setSelectedIds(new Set());
    setLastClickedId(null);
  }, [parentId]);

  const openFolder = (node) => {
    if (node.kind !== FILES_KIND_FOLDER) return;
    setFolderStack((prev) => [...prev, { id: node.id, name: node.name }]);
  };

  const goToBreadcrumb = (index) => {
    if (index < 0) {
      setFolderStack([]);
      return;
    }
    setFolderStack((prev) => prev.slice(0, index + 1));
  };

  const handleCreateFolder = async () => {
    if (!companyId || !newFolderName.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      await createFilesFolder({
        companyId,
        name: newFolderName,
        ownerScope,
        ownerUid: ownerScope === FILES_OWNER_SCOPE_USER ? uid : null,
        parentId,
        uid,
      });
      setNewFolderName('');
      await refresh();
    } catch (err) {
      console.error('FilesHub create folder failed', err);
      setError(err?.message || 'Could not create folder.');
    } finally {
      setBusy(false);
    }
  };

  const uploadFilesInto = async (files, targetParentId) => {
    if (!companyId || !files?.length) return;
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const result = await runBulkFilesOperation({
        items: Array.from(files),
        runOne: (file) =>
          uploadFilesNode({
            companyId,
            file,
            fileName: file.name,
            ownerScope,
            ownerUid: ownerScope === FILES_OWNER_SCOPE_USER ? uid : null,
            parentId: targetParentId,
            uid,
          }),
      });
      setInfo(formatBulkFilesResultMessage(result, 'uploaded'));
      if (result.failed.length) {
        setError(formatBulkFilesResultMessage(result, 'uploaded'));
      }
      await refresh();
    } catch (err) {
      console.error('FilesHub upload failed', err);
      setError(err?.message || 'Could not upload file.');
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async (event) => {
    const list = event.target.files;
    event.target.value = '';
    if (!list?.length || busy) return;
    await uploadFilesInto(list, parentId);
  };

  const handleDelete = async (node) => {
    if (!companyId || busy) return;
    const label = node.kind === FILES_KIND_FOLDER ? 'folder' : 'file';
    if (!window.confirm(`Delete this ${label}?`)) return;
    setBusy(true);
    setError('');
    try {
      await deleteFilesNode({ companyId, node, uid, role });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(node.id);
        return next;
      });
      await refresh();
    } catch (err) {
      console.error('FilesHub delete failed', err);
      setError(err?.message || 'Could not delete.');
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = (node) => {
    if (!node.downloadUrl) return;
    window.open(node.downloadUrl, '_blank', 'noopener,noreferrer');
  };

  const selectedNodes = useMemo(
    () => nodes.filter((n) => selectedIds.has(n.id)),
    [nodes, selectedIds],
  );

  const toggleSelect = (nodeId, event) => {
    const orderedIds = nodes.map((n) => n.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (event?.shiftKey && lastClickedId) {
        const a = orderedIds.indexOf(lastClickedId);
        const b = orderedIds.indexOf(nodeId);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let i = lo; i <= hi; i += 1) next.add(orderedIds[i]);
          return next;
        }
      }
      if (event?.ctrlKey || event?.metaKey) {
        if (next.has(nodeId)) next.delete(nodeId);
        else next.add(nodeId);
        return next;
      }
      if (next.has(nodeId) && next.size === 1) {
        next.clear();
      } else {
        next.clear();
        next.add(nodeId);
      }
      return next;
    });
    setLastClickedId(nodeId);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === nodes.length) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(nodes.map((n) => n.id)));
  };

  const moveNodesTo = async (items, targetParentId) => {
    if (!companyId || !items.length) return;
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const result = await runBulkFilesOperation({
        items,
        runOne: (node) =>
          moveFilesNode({
            companyId,
            node,
            targetParentId,
            uid,
            role,
          }),
      });
      const msg = formatBulkFilesResultMessage(result, 'moved');
      if (result.failed.length) setError(msg);
      else setInfo(msg);
      setSelectedIds(new Set());
      await refresh();
    } catch (err) {
      console.error('FilesHub move failed', err);
      setError(err?.message || 'Could not move.');
    } finally {
      setBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedNodes.length || busy) return;
    if (
      !window.confirm(
        `Delete ${selectedNodes.length} selected item${
          selectedNodes.length === 1 ? '' : 's'
        }? This cannot be undone.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const result = await runBulkFilesOperation({
        items: selectedNodes,
        runOne: (node) => deleteFilesNode({ companyId, node, uid, role }),
      });
      const msg = formatBulkFilesResultMessage(result, 'deleted');
      if (result.failed.length) setError(msg);
      else setInfo(msg);
      setSelectedIds(new Set());
      await refresh();
    } catch (err) {
      console.error('FilesHub bulk delete failed', err);
      setError(err?.message || 'Could not delete.');
    } finally {
      setBusy(false);
    }
  };

  const handleBulkCopy = async () => {
    if (!selectedNodes.length || busy) return;
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const result = await runBulkFilesOperation({
        items: selectedNodes,
        runOne: (node) =>
          copyFilesNode({
            companyId,
            node,
            targetParentId: parentId,
            uid,
            role,
          }),
      });
      const msg = formatBulkFilesResultMessage(result, 'copied');
      if (result.failed.length) setError(msg);
      else setInfo(msg);
      await refresh();
    } catch (err) {
      console.error('FilesHub bulk copy failed', err);
      setError(err?.message || 'Could not copy.');
    } finally {
      setBusy(false);
    }
  };

  const loadMoveTargets = async () => {
    if (!companyId) return;
    setMoveTargetsLoading(true);
    try {
      /** @type {Array<{ id: string, name: string, depth: number }>} */
      const flat = [{ id: FILES_ROOT_PARENT_ID, name: 'Root', depth: 0 }];
      const walk = async (folderId, depth) => {
        const children = await listFilesChildren({
          companyId,
          ownerScope,
          ownerUid: ownerScope === FILES_OWNER_SCOPE_USER ? uid : null,
          parentId: folderId,
        });
        for (const child of children) {
          if (child.kind !== FILES_KIND_FOLDER) continue;
          flat.push({ id: child.id, name: child.name, depth });
          await walk(child.id, depth + 1);
        }
      };
      await walk(FILES_ROOT_PARENT_ID, 1);
      setMoveTargets(flat);
      setMoveDestId(parentId);
    } catch (err) {
      console.error('FilesHub move targets failed', err);
      setError(err?.message || 'Could not load folders.');
    } finally {
      setMoveTargetsLoading(false);
    }
  };

  const openMoveDialog = async () => {
    if (!selectedNodes.length) return;
    setMoveDialogOpen(true);
    await loadMoveTargets();
  };

  const confirmMoveDialog = async () => {
    setMoveDialogOpen(false);
    await moveNodesTo(selectedNodes, moveDestId);
  };

  const onInternalDragStart = (event, node) => {
    const ids = selectedIds.has(node.id)
      ? Array.from(selectedIds)
      : [node.id];
    const payload = nodes.filter((n) => ids.includes(n.id));
    dragPayloadRef.current = payload;
    setDraggingInternal(true);
    event.dataTransfer.setData(
      INTERNAL_DRAG_MIME,
      JSON.stringify(payload.map((n) => n.id)),
    );
    event.dataTransfer.effectAllowed = 'move';
  };

  const onInternalDragEnd = () => {
    setDraggingInternal(false);
    setDropTargetId(null);
    dragPayloadRef.current = [];
  };

  const resolveInternalDragItems = (event) => {
    if (dragPayloadRef.current.length) return dragPayloadRef.current;
    try {
      const raw = event.dataTransfer.getData(INTERNAL_DRAG_MIME);
      if (!raw) return [];
      const ids = JSON.parse(raw);
      return nodes.filter((n) => ids.includes(n.id));
    } catch {
      return [];
    }
  };

  const onFolderDragOver = (event, folderId) => {
    const internal = draggingInternal || event.dataTransfer.types.includes(INTERNAL_DRAG_MIME);
    const osFiles = dataTransferHasFiles(event.dataTransfer);
    if (!internal && !osFiles) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = internal ? 'move' : 'copy';
    setDropTargetId(folderId);
  };

  const onCurrentViewDragOver = (event) => {
    const internal =
      draggingInternal || event.dataTransfer.types.includes(INTERNAL_DRAG_MIME);
    const osFiles = dataTransferHasFiles(event.dataTransfer);
    if (!internal && !osFiles) return;
    // Prefer folder-row targets when hovering a child folder.
    if (dropTargetId && dropTargetId !== '__current__') return;
    event.preventDefault();
    event.dataTransfer.dropEffect = osFiles ? 'copy' : 'move';
    setDropTargetId('__current__');
  };

  const onDragLeavePanel = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDropTargetId(null);
    }
  };

  const onDropOnFolder = async (event, folderId) => {
    event.preventDefault();
    event.stopPropagation();
    setDropTargetId(null);
    if (busy) return;

    if (dataTransferHasFiles(event.dataTransfer) && event.dataTransfer.files?.length) {
      await uploadFilesInto(event.dataTransfer.files, folderId);
      onInternalDragEnd();
      return;
    }

    const items = resolveInternalDragItems(event).filter((n) => n.id !== folderId);
    onInternalDragEnd();
    if (!items.length) return;
    await moveNodesTo(items, folderId);
  };

  const onDropOnCurrentView = async (event) => {
    event.preventDefault();
    setDropTargetId(null);
    if (busy) return;

    if (dataTransferHasFiles(event.dataTransfer) && event.dataTransfer.files?.length) {
      await uploadFilesInto(event.dataTransfer.files, parentId);
      onInternalDragEnd();
      return;
    }

    const items = resolveInternalDragItems(event);
    onInternalDragEnd();
    if (!items.length) return;
    await moveNodesTo(items, parentId);
  };

  const scopeLabel = useMemo(
    () =>
      ownerScope === FILES_OWNER_SCOPE_COMPANY ? 'Company Files' : 'My files',
    [ownerScope],
  );

  if (!companyId) {
    return (
      <Alert severity="info">Select a company workspace to use Files.</Alert>
    );
  }

  const allSelected = nodes.length > 0 && selectedIds.size === nodes.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <Stack spacing={2} sx={{ maxWidth: 960 }}>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          Files
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Shared company library and your personal files. Drag rows to move,
          drop files from your computer to upload.
        </Typography>
      </Box>

      <Tabs
        value={scopeTab}
        onChange={(_e, next) => setScopeTab(next)}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Company Files" sx={{ textTransform: 'none' }} />
        <Tab label="My files" sx={{ textTransform: 'none' }} />
      </Tabs>

      {error ? (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}
      {info ? (
        <Alert severity="success" onClose={() => setInfo('')}>
          {info}
        </Alert>
      ) : null}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          size="small"
          label="New folder"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          disabled={busy}
          sx={{ minWidth: 180 }}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<CreateNewFolderOutlinedIcon />}
          onClick={handleCreateFolder}
          disabled={busy || !newFolderName.trim()}
          sx={{ textTransform: 'none' }}
        >
          Create folder
        </Button>
        <Button
          size="small"
          variant="contained"
          component="label"
          startIcon={<UploadFileOutlinedIcon />}
          disabled={busy}
          sx={{ textTransform: 'none' }}
        >
          Upload
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            onChange={handleUpload}
          />
        </Button>
        {busy ? <CircularProgress size={18} /> : null}
      </Stack>

      {selectedNodes.length > 0 ? (
        <Paper
          variant="outlined"
          sx={{ px: 1.5, py: 1, bgcolor: 'action.selected' }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Typography variant="body2" fontWeight={600}>
              {selectedNodes.length} selected
            </Typography>
            <Button
              size="small"
              startIcon={<DriveFileMoveOutlinedIcon />}
              onClick={openMoveDialog}
              disabled={busy}
              sx={{ textTransform: 'none' }}
            >
              Move
            </Button>
            <Button
              size="small"
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={handleBulkCopy}
              disabled={busy}
              sx={{ textTransform: 'none' }}
            >
              Copy
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleBulkDelete}
              disabled={busy}
              sx={{ textTransform: 'none' }}
            >
              Delete
            </Button>
            <Button
              size="small"
              onClick={() => setSelectedIds(new Set())}
              disabled={busy}
              sx={{ textTransform: 'none' }}
            >
              Clear
            </Button>
          </Stack>
        </Paper>
      ) : null}

      <Breadcrumbs aria-label="Files path">
        <Link
          component="button"
          type="button"
          underline="hover"
          color="inherit"
          onClick={() => goToBreadcrumb(-1)}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          <HomeOutlinedIcon fontSize="small" />
          {scopeLabel}
        </Link>
        {folderStack.map((folder, index) => (
          <Link
            key={folder.id}
            component="button"
            type="button"
            underline="hover"
            color={
              index === folderStack.length - 1 ? 'text.primary' : 'inherit'
            }
            onClick={() => goToBreadcrumb(index)}
          >
            {folder.name}
          </Link>
        ))}
      </Breadcrumbs>

      <Paper
        variant="outlined"
        sx={{
          minHeight: 280,
          outline:
            dropTargetId === '__current__'
              ? '2px solid'
              : undefined,
          outlineColor:
            dropTargetId === '__current__' ? 'primary.main' : undefined,
          bgcolor:
            dropTargetId === '__current__' ? 'action.hover' : undefined,
        }}
        onDragOver={onCurrentViewDragOver}
        onDragLeave={onDragLeavePanel}
        onDrop={onDropOnCurrentView}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : nodes.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 2, py: 4, textAlign: 'center' }}
          >
            This folder is empty. Create a folder, upload, or drop files here.
          </Typography>
        ) : (
          <List dense disablePadding>
            <ListItem dense divider>
              <Checkbox
                size="small"
                edge="start"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleSelectAll}
                inputProps={{ 'aria-label': 'Select all' }}
              />
              <ListItemText
                primaryTypographyProps={{ variant: 'caption' }}
                primary="Select all"
              />
            </ListItem>
            {nodes.map((node) => {
              const isFolder = node.kind === FILES_KIND_FOLDER;
              const selected = selectedIds.has(node.id);
              const isDropTarget = dropTargetId === node.id && isFolder;
              return (
                <ListItem
                  key={node.id}
                  divider
                  draggable={!busy}
                  onDragStart={(e) => onInternalDragStart(e, node)}
                  onDragEnd={onInternalDragEnd}
                  onDragOver={
                    isFolder
                      ? (e) => {
                          e.stopPropagation();
                          onFolderDragOver(e, node.id);
                        }
                      : undefined
                  }
                  onDragLeave={
                    isFolder
                      ? () => {
                          setDropTargetId((cur) =>
                            cur === node.id ? null : cur,
                          );
                        }
                      : undefined
                  }
                  onDrop={
                    isFolder
                      ? (e) => onDropOnFolder(e, node.id)
                      : undefined
                  }
                  sx={{
                    bgcolor: isDropTarget
                      ? 'primary.light'
                      : selected
                        ? 'action.selected'
                        : undefined,
                    opacity: draggingInternal && selected ? 0.7 : 1,
                    '& .MuiListItemSecondaryAction-root': { right: 8 },
                  }}
                  secondaryAction={
                    <Stack direction="row" spacing={0.25}>
                      {node.kind === FILES_KIND_FILE && node.downloadUrl ? (
                        <Tooltip title="Download">
                          <IconButton
                            edge="end"
                            size="small"
                            aria-label="Download"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(node);
                            }}
                          >
                            <DownloadOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Delete">
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(node);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                >
                  <Checkbox
                    size="small"
                    edge="start"
                    checked={selected}
                    tabIndex={-1}
                    disableRipple
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelect(node.id, e);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    inputProps={{ 'aria-label': `Select ${node.name}` }}
                  />
                  <ListItemButton
                    sx={{ py: 0.5, pr: 8 }}
                    onClick={(e) => {
                      if (e.shiftKey || e.ctrlKey || e.metaKey) {
                        toggleSelect(node.id, e);
                        return;
                      }
                      if (isFolder) openFolder(node);
                      else toggleSelect(node.id, e);
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {isFolder ? (
                        <FolderOutlinedIcon fontSize="small" />
                      ) : (
                        <InsertDriveFileOutlinedIcon fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={node.name}
                      secondary={
                        node.kind === FILES_KIND_FILE
                          ? [
                              node.mimeType,
                              typeof node.bytes === 'number'
                                ? `${Math.max(
                                    1,
                                    Math.round(node.bytes / 1024),
                                  )} KB`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')
                          : 'Folder — drop here to move/upload'
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>

      <Dialog
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Move to folder</DialogTitle>
        <DialogContent dividers>
          {moveTargetsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List dense>
              {moveTargets.map((t) => (
                <ListItemButton
                  key={t.id || 'root'}
                  selected={moveDestId === t.id}
                  onClick={() => setMoveDestId(t.id)}
                  sx={{ pl: 1 + t.depth * 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <FolderOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t.name} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={confirmMoveDialog}
            disabled={busy || moveTargetsLoading}
            sx={{ textTransform: 'none' }}
          >
            Move here
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
